import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpErrorResponse, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { catchError, endWith, exhaustMap, from, map, mergeMap, Observable, of, startWith, withLatestFrom } from 'rxjs';
import { CategoryFolderEnum } from '@shared/enums/categorie.enum';
import { Dispositif } from '@features/referentiel/models/dispositif';
import { MesRemboursemntsRechercheForm } from '@features/mes-remboursements/features/mes-remboursements-recherche/models/remboursement-recherche-form';
import { RechercheForm } from '@features/mes-financements/features/mes-financements-recherche/models/recherche-form';
import { getFilename, openFile } from '../files/download-file';

type AnyForm = RechercheForm | MesRemboursemntsRechercheForm;

export type ExportContext<TForm extends AnyForm = AnyForm> = {
    sirets: string[];
    form: TForm;
    categorieDossier: CategoryFolderEnum;
    dispositifs: Dispositif[];
    total: number;
};

export type ExportSelectors<TForm extends AnyForm = AnyForm> = {
    sirets$: Observable<string[]>;
    form$: Observable<TForm>;
    categorie$: Observable<CategoryFolderEnum>;
    dispositifs$: Observable<Dispositif[]>;
    total$: Observable<number>;
};

export function isNoContent(res: HttpResponse<Blob>): boolean {
    return res.status === HttpStatusCode.NoContent || !res.body || (res.body instanceof Blob && res.body.size === 0);
}

export function parseBlobError(err: any, translate: TranslateService) {
    const timeoutMessage = translate.instant('toast.export.timeout');

    if (err instanceof HttpErrorResponse && [0, HttpStatusCode.RequestTimeout, HttpStatusCode.GatewayTimeout].includes(err.status)) {
        return of(timeoutMessage);
    }

    const fallback = translate.instant('error.retry');
    if (err?.error instanceof Blob) {
        return from(err.error.text()).pipe(
            map(text => {
                try {
                    const json = JSON.parse(String(text));
                    return json?.message ?? err.message ?? fallback;
                } catch {
                    return err.message ?? fallback;
                }
            }),
            catchError(() => of(err?.message ?? fallback))
        );
    }
    return of(err);
}

export type LoadingActions = {
    start?: (payload: { isLoading: boolean }) => any;
    stop?: (payload: { isLoading: boolean }) => any;
};

export type ExportFactoryOpts<TCriteria, TService> = {
    trigger: any; // action creator
    success: (payload?: any) => any;
    failure: (payload: { error: any }) => any;

    // selectors bundle
    selectors: () => ExportSelectors<AnyForm>;

    // how to build criteria
    criteriaBuilder: (args: {
        sirets: string[];
        form: any;
        categorieDossier: any;
        dispositifs: any[];
    }) => TCriteria;

    // how to call the API
    call: (svc: TService, criteria: TCriteria) => Observable<HttpResponse<Blob>>;

    // inject a service (financements/remboursements)
    injectService: () => TService;

    // optional loading toggles
    loading?: LoadingActions;
};

export function createExportEffectFactory<TCriteria, TService>(opts: ExportFactoryOpts<TCriteria, TService>) {
    return createEffect(
        (
            actions$ = inject(Actions),
            translate = inject(TranslateService),
            svc = opts.injectService(),
        ) => {
            const { sirets$, form$, categorie$, dispositifs$, total$ } = opts.selectors();

            return actions$.pipe(
                ofType(opts.trigger),
                withLatestFrom(sirets$, form$, categorie$, dispositifs$, total$),
                exhaustMap(([_, sirets, form, categorieDossier, dispositifs, total]) => {
                    if (!total || total <= 0) {
                        return of(opts.failure({ error: null }));
                    }

                    const siretForm = form?.siret ? [form.siret] : sirets;
                    const criteria = opts.criteriaBuilder({ sirets: siretForm, form, categorieDossier, dispositifs });

                    return opts.call(svc, criteria).pipe(
                        mergeMap((res: HttpResponse<Blob>) => {
                            if (isNoContent(res)) {
                                return of(opts.failure({ error: null }));
                            }
                            const name = getFilename(res) ?? `export_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
                            openFile(res.body!, name);
                            return of(opts.success());
                        }),
                        catchError((err) =>
                            parseBlobError(err, translate).pipe(
                                map((message) => opts.failure({ error: message }))
                            )
                        ),
                        startWith(opts.loading?.start ? opts.loading.start({ isLoading: true }) : undefined),
                        endWith(opts.loading?.stop ? opts.loading.stop({ isLoading: false }) : undefined)
                    );
                })
            );
        },
        { functional: true }
    );
}